import { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import type { SearchSuggestion } from '../../favorites-app/types';
import type { InvestingStyleId, OnboardingPreferences, OnboardingStepId } from '../types';
import { trackMixpanelEvent } from '~/ui-apps/utilities/mixpanelTracking';
import {
  TOTAL_ONBOARDING_STEPS,
  ONBOARDING_FLOW_VERSION,
  MIN_REQUIRED_FAVORITES,
  MIN_REQUIRED_SIGNALS,
  STEP_COPY,
  getOnboardingCompletionDurationMs,
} from '../constants';
import { ALL_SIGNAL_IDS } from '../data/mockOnboardingCatalog';
import { buildInvestingStyleQueriesPreference } from '../data/investingStyleScreenerQueries';
import { buildSignalQueriesPreference } from '../data/signalScreenerQueries';
import { ALL_POPULAR_SEARCH_IDS } from '../data/popularSearchCatalog';
import { syncOnboardingChecklists } from '../utilities/syncOnboardingChecklists';
import { syncOnboardingFavorites } from '../utilities/syncOnboardingFavorites';
// import { syncOnboardingPopularSearches } from '../utilities/syncOnboardingPopularSearches';
import { POST_AUTH_LANDING_PATH } from '~/utilities/postAuthLanding';

const ONBOARDING_FINISH_REDIRECT_PATH = POST_AUTH_LANDING_PATH;

export type WizardStep = 1 | 2 | 3 | 4 | 'completing';

interface UseOnboardingWizardProps {
  userId?: number;
  initialStep?: number;
  initialPreferences?: OnboardingPreferences;
  onComplete: () => void;
  isPreferenceEditMode?: boolean;
}

/** Loader already applies resolveOnboardingResumeStep; only clamp to valid wizard steps. */
function clampInitialStep(step: number): WizardStep {
  if (!Number.isInteger(step) || step < 1) {
    return 1;
  }
  return Math.min(step, TOTAL_ONBOARDING_STEPS) as WizardStep;
}

function isFavoriteTickerRow(value: unknown): value is {
  ticker_id: number;
  ticker_symbol: string;
  name: string;
  logo_url?: string | null;
  exchange?: string | null;
  company_id: number;
} {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.ticker_symbol === 'string' &&
    typeof row.name === 'string' &&
    (typeof row.ticker_id === 'number' || typeof row.ticker_id === 'string') &&
    (typeof row.company_id === 'number' || typeof row.company_id === 'string')
  );
}

function favoriteToSearchSuggestion(row: {
  ticker_id: number;
  ticker_symbol: string;
  name: string;
  logo_url?: string | null;
  exchange?: string | null;
  company_id: number;
}): SearchSuggestion {
  return {
    ticker_symbol: row.ticker_symbol,
    name: row.name,
    market_cap: 0,
    logo_url: row.logo_url ?? '',
    ticker_id: String(row.ticker_id),
    exchange: row.exchange ?? '',
    company_id: String(row.company_id),
  };
}

function sortCompaniesAlphabetically(companies: SearchSuggestion[]): SearchSuggestion[] {
  return [...companies].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );
}

function normalizeInvestingStyles(
  raw: OnboardingPreferences['investingStyle'],
): InvestingStyleId[] {
  if (!raw) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw;
  }
  return [];
}

function mergeSearchSuggestions(
  existing: SearchSuggestion[],
  fromFavorites: SearchSuggestion[],
): SearchSuggestion[] {
  const bySymbol = new Map<string, SearchSuggestion>();
  for (const stock of fromFavorites) {
    bySymbol.set(stock.ticker_symbol.toLowerCase(), stock);
  }
  for (const stock of existing) {
    bySymbol.set(stock.ticker_symbol.toLowerCase(), stock);
  }
  return sortCompaniesAlphabetically(Array.from(bySymbol.values()));
}

export function useOnboardingWizard({
  userId,
  initialStep = 1,
  initialPreferences = {},
  onComplete,
  isPreferenceEditMode = false,
}: UseOnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>(() => clampInitialStep(initialStep));
  const [selectedInvestingStyles, setSelectedInvestingStyles] = useState<InvestingStyleId[]>(() =>
    normalizeInvestingStyles(initialPreferences.investingStyle),
  );
  const [selectedCompanies, setSelectedCompanies] = useState<SearchSuggestion[]>([]);
  const [selectedSignals, setSelectedSignals] = useState<string[]>(
    initialPreferences.signals ?? [],
  );
  const [selectedThemes, setSelectedThemes] = useState<string[]>(
    initialPreferences.themes ?? [],
  );
  const [selectedPopularSearches, setSelectedPopularSearches] = useState<string[]>(
    initialPreferences.popularSearches ?? [],
  );
  const [skippedSteps, setSkippedSteps] = useState<OnboardingStepId[]>(
    initialPreferences.skippedSteps ?? [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(() => Boolean(userId));
  const [error, setError] = useState<string | null>(null);

  const preferences = useMemo<OnboardingPreferences>(
    () => ({
      investingStyle: selectedInvestingStyles,
      investingStyleQueries: buildInvestingStyleQueriesPreference(selectedInvestingStyles),
      signals: selectedSignals,
      signalQueries: buildSignalQueriesPreference(selectedSignals),
      themes: selectedThemes,
      popularSearches: selectedPopularSearches,
      skippedSteps,
    }),
    [selectedInvestingStyles, selectedSignals, selectedThemes, selectedPopularSearches, skippedSteps],
  );

  useEffect(() => {
    if (!userId || currentStep !== 1) {
      return;
    }

    const abortController = new AbortController();

    const loadExistingFavorites = async () => {
      setIsLoadingFavorites(true);
      try {
        const response = await axios.get<{ success?: boolean; data?: unknown[] }>(
          `/api/users/favorites/${userId}`,
          { signal: abortController.signal },
        );

        if (!response.data.success || !Array.isArray(response.data.data)) {
          return;
        }

        const fromFavorites = response.data.data
          .filter(isFavoriteTickerRow)
          .map(favoriteToSearchSuggestion);

        setSelectedCompanies((prev) => mergeSearchSuggestions(prev, fromFavorites));
      } catch (err: unknown) {
        if (abortController.signal.aborted || axios.isCancel(err)) {
          return;
        }
        console.error('Failed to load onboarding favorites:', err);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingFavorites(false);
        }
      }
    };

    void loadExistingFavorites();

    return () => abortController.abort();
  }, [userId, currentStep]);

  const toggleInvestingStyle = useCallback((id: InvestingStyleId) => {
    const isSelecting = !selectedInvestingStyles.includes(id);
    setSelectedInvestingStyles((prev) =>
      prev.includes(id) ? prev.filter((styleId) => styleId !== id) : [...prev, id],
    );
    setSkippedSteps((prev) => prev.filter((stepId) => stepId !== 'investingStyle'));
    if (isSelecting) {
      setError(null);
    }
  }, [selectedInvestingStyles]);

  const addCompany = useCallback((stock: SearchSuggestion) => {
    setSelectedCompanies((prev) => {
      if (prev.some((s) => s.ticker_symbol.toLowerCase() === stock.ticker_symbol.toLowerCase())) {
        return prev;
      }
      return sortCompaniesAlphabetically([...prev, stock]);
    });
    setError(null);
  }, []);

  const removeCompany = useCallback((tickerSymbol: string) => {
    setSelectedCompanies((prev) =>
      sortCompaniesAlphabetically(
        prev.filter((s) => s.ticker_symbol.toLowerCase() !== tickerSymbol.toLowerCase()),
      ),
    );
  }, []);

  const toggleSignal = useCallback((id: string) => {
    setSelectedSignals((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  }, []);

  const clearSelectedSignals = useCallback(() => {
    setSelectedSignals([]);
  }, []);

  const selectAllSignals = useCallback(() => {
    setSelectedSignals([...ALL_SIGNAL_IDS]);
  }, []);

  const togglePopularSearch = useCallback((id: string) => {
    setSelectedPopularSearches((prev) =>
      prev.includes(id) ? prev.filter((searchId) => searchId !== id) : [...prev, id],
    );
  }, []);

  const clearSelectedPopularSearches = useCallback(() => {
    setSelectedPopularSearches([]);
  }, []);

  const selectAllPopularSearches = useCallback(() => {
    setSelectedPopularSearches([...ALL_POPULAR_SEARCH_IDS]);
  }, []);

  const saveProgress = useCallback(
    async (nextStep: number, prefs: OnboardingPreferences) => {
      await axios.post('/api/users/onboarding-progress', {
        step: nextStep,
        preferences: { ...prefs, onboardingFlowVersion: ONBOARDING_FLOW_VERSION },
      });
    },
    [],
  );

  const syncCompaniesFavorites = useCallback(async () => {
    if (!userId) {
      return;
    }
    await syncOnboardingFavorites(userId, selectedCompanies);
  }, [userId, selectedCompanies]);

  const trackStep = useCallback(
    (event: string, extra: Record<string, unknown> = {}) => {
      if (userId) {
        trackMixpanelEvent(event, userId, { step: currentStep, ...extra });
      }
    },
    [userId, currentStep],
  );

  const syncSignalsToChecklists = useCallback(async () => {
    if (!userId) {
      return;
    }
    await syncOnboardingChecklists(userId, selectedSignals);
  }, [userId, selectedSignals]);

  // const syncPopularSearchesToScreeners = useCallback(async () => {
  //   if (!userId) {
  //     return;
  //   }
  //   await syncOnboardingPopularSearches(userId, selectedPopularSearches);
  // }, [userId, selectedPopularSearches]);

  const persistOnboardingData = useCallback(async () => {
    if (!userId) {
      return;
    }

    // Popular-searches step is temporarily commented out — do not sync (or clear) screeners.
    // await Promise.all([syncSignalsToChecklists(), syncPopularSearchesToScreeners()]);
    await syncSignalsToChecklists();
  }, [userId, syncSignalsToChecklists]);

  const finishOnboarding = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (isPreferenceEditMode) {
        await saveProgress(TOTAL_ONBOARDING_STEPS, preferences);
        trackStep('Onboarding Preferences Updated', {
          favorite_count: selectedCompanies.length,
          signal_count: selectedSignals.length,
          theme_count: selectedThemes.length,
          popular_search_count: selectedPopularSearches.length,
          investing_style: selectedInvestingStyles,
        });
        setIsSubmitting(false);
        onComplete();
        return;
      }

      await axios.post('/api/users/complete-onboarding', {
        status: 'completed',
        preferences: { ...preferences, onboardingFlowVersion: ONBOARDING_FLOW_VERSION },
      });

      trackStep('Onboarding Completed', {
        favorite_count: selectedCompanies.length,
        signal_count: selectedSignals.length,
        theme_count: selectedThemes.length,
        popular_search_count: selectedPopularSearches.length,
        investing_style: selectedInvestingStyles,
        redirect_path: ONBOARDING_FINISH_REDIRECT_PATH,
      });

      window.location.replace(ONBOARDING_FINISH_REDIRECT_PATH);
    } catch (err: unknown) {
      console.error('Onboarding complete failed:', err);
      const message =
        axios.isAxiosError(err) && err.response?.data?.error
          ? String(err.response.data.error)
          : 'Something went wrong. Please try again.';
      setError(message);
      setIsSubmitting(false);
      setCurrentStep(TOTAL_ONBOARDING_STEPS);
    }
  }, [
    preferences,
    selectedCompanies.length,
    selectedSignals,
    selectedThemes,
    selectedPopularSearches,
    selectedInvestingStyles,
    isPreferenceEditMode,
    saveProgress,
    onComplete,
    trackStep,
  ]);

  const runCompletion = useCallback(async () => {
    setCurrentStep('completing');
    setIsSubmitting(true);
    setError(null);

    try {
      await Promise.all([
        new Promise((resolve) => setTimeout(resolve, getOnboardingCompletionDurationMs())),
        persistOnboardingData(),
      ]);
      await finishOnboarding();
    } catch (err: unknown) {
      console.error('Onboarding completion failed:', err);
      const message =
        axios.isAxiosError(err) && err.response?.data?.error
          ? String(err.response.data.error)
          : 'Something went wrong. Please try again.';
      setError(message);
      setIsSubmitting(false);
      setCurrentStep(TOTAL_ONBOARDING_STEPS);
    }
  }, [persistOnboardingData, finishOnboarding]);

  const goNext = useCallback(async () => {
    setError(null);

    try {
      if (currentStep === 1) {
        if (selectedCompanies.length < MIN_REQUIRED_FAVORITES) {
          setError(STEP_COPY.companies.minCompaniesError);
          return;
        }
        setIsSubmitting(true);
        await syncCompaniesFavorites();
        const prefs: OnboardingPreferences = { ...preferences, skippedSteps };
        await saveProgress(2, prefs);
        trackStep('Onboarding Step Completed', { step_name: 'companies' });
        setCurrentStep(2);
        setIsSubmitting(false);
        return;
      }

      if (currentStep === 2) {
        if (selectedInvestingStyles.length === 0) {
          setError('Please select at least one investing style.');
          return;
        }
        setIsSubmitting(true);
        const prefs: OnboardingPreferences = { ...preferences, skippedSteps };
        await saveProgress(3, prefs);
        trackStep('Onboarding Step Completed', { step_name: 'investingStyle' });
        setCurrentStep(3);
        setIsSubmitting(false);
        return;
      }

      if (currentStep === 3) {
        if (selectedSignals.length < MIN_REQUIRED_SIGNALS) {
          setError(STEP_COPY.signals.minSignalsError);
          return;
        }
        setIsSubmitting(true);
        await syncSignalsToChecklists();
        await saveProgress(TOTAL_ONBOARDING_STEPS, preferences);
        trackStep('Onboarding Step Completed', { step_name: 'signals' });
        await runCompletion();
        return;
      }

      // if (currentStep === 4) {
      //   trackStep('Onboarding Step Completed', { step_name: 'popularSearches' });
      //   await runCompletion();
      // }
    } catch (err: unknown) {
      console.error('Onboarding step failed:', err);
      const message =
        axios.isAxiosError(err) && err.response?.data?.error
          ? String(err.response.data.error)
          : err instanceof Error
            ? err.message
            : 'Something went wrong. Please try again.';
      setError(message);
      setIsSubmitting(false);
    }
  }, [
    currentStep,
    selectedInvestingStyles,
    selectedSignals.length,
    selectedCompanies.length,
    syncCompaniesFavorites,
    preferences,
    skippedSteps,
    saveProgress,
    syncSignalsToChecklists,
    trackStep,
    runCompletion,
  ]);

  const goBack = useCallback(() => {
    if (typeof currentStep === 'number' && currentStep > 1) {
      setCurrentStep((currentStep - 1) as WizardStep);
      setError(null);
    }
  }, [currentStep]);

  const canGoBack = typeof currentStep === 'number' && currentStep > 1;

  return {
    currentStep,
    selectedInvestingStyles,
    toggleInvestingStyle,
    selectedCompanies,
    addCompany,
    removeCompany,
    selectedSignals,
    toggleSignal,
    clearSelectedSignals,
    selectAllSignals,
    selectedThemes,
    selectedPopularSearches,
    togglePopularSearch,
    clearSelectedPopularSearches,
    selectAllPopularSearches,
    isSubmitting,
    isLoadingFavorites,
    error,
    goNext,
    goBack,
    canGoBack,
  };
}
